import React, { useCallback } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GhostButton } from '@ui/GhostButton';
import { Icon } from '@ui/Icon/Icon';
import type { CandidateRow } from '@shared/models/element-import.model';
import type { AiImportMappingActions } from './use-ai-import-mapping.hook';
import { aiImportMappingTooltip } from './ai-import-mapping-apply.helpers';

type CandidateAiMappingButtonProps = {
  row: CandidateRow;
  aiMapping: AiImportMappingActions;
};

type ClickHandler = React.MouseEventHandler<HTMLButtonElement>;

export const CandidateAiMappingButton: React.FC<
  CandidateAiMappingButtonProps
> = ({ row, aiMapping }) => {
  const { classes } = useStyles();
  const isLoading = aiMapping.isMapping(row.id);
  const targetCount = aiMapping.targetCountFromRow(row);

  const handleButtonClick = useCallback<ClickHandler>(
    (event) => {
      event.stopPropagation();
      aiMapping.mapFromRow(row);
    },
    [aiMapping, row],
  );

  return (
    <Box
      className={classes.hoverAction}
      data-candidate-row-hover-action
      data-open={isLoading ? '' : undefined}
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip
        title={aiImportMappingTooltip(targetCount, { busy: isLoading })}
        placement="top"
        disableInteractive
      >
        <span>
          <GhostButton disabled={isLoading} onClick={handleButtonClick}>
            {isLoading ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <Icon category="mui" icon="AutoAwesome" size="small" />
            )}
          </GhostButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  hoverAction: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    marginLeft: -2,
    padding: '4px 2px 4px 0',
    pointerEvents: 'none',
    visibility: 'hidden',
  },
}));
